import { useEffect, useRef } from 'react';
import { useSEO, useSchemaLD } from '@/hooks/useSEO';

const benefits = [
  {
    icon: 'ri-store-2-line',
    title: 'Transforme seu box em vitrine de alto padrão',
    description: 'Balcões funcionais, expositores que valorizam a mercadoria e layout que guia o cliente até a compra. Seu box vende mais sem precisar de mais espaço.',
  },
  {
    icon: 'ri-speed-line',
    title: 'Montagem rápida para não perder dia de feira',
    description: 'Nossa equipe própria instala em tempo recorde. Você não perde um dia de venda. Combinamos o horário, chegamos, montamos e entregamos pronto para abrir.',
  },
  {
    icon: 'ri-ruler-2-line',
    title: 'Aproveitamento de cada centímetro do box',
    description: 'Projeto 3D gratuito que mostra exatamente como vai ficar. Cada prateleira, cada arara, cada balcão posicionado para maximizar o espaço e o fluxo de clientes.',
  },
  {
    icon: 'ri-money-dollar-circle-line',
    title: 'Preço justo do produtor para o lojista',
    description: 'Sem intermediários. Você negocia direto com quem fabrica. Pagamento facilitado e prazos que respeitam o fluxo de caixa do comerciante do polo.',
  },
];

const blocks = [
  {
    title: 'Balcão de Atendimento que Expõe, Organiza e Fecha Venda',
    text: [
      'No polo de confecções de Santa Cruz do Capibaribe, o cliente decide em segundos se entra no box ou passa reto. O balcão é o primeiro elemento que ele vê — e precisa comunicar qualidade, organização e profissionalismo antes de qualquer palavra ser dita. Nossos balcões têm vitrine frontal de vidro temperado para expor as peças mais vendidas, tampo em MDF de 18mm revestido em HPL resistente a riscos e umidade, e gavetas organizadas para notas, cartões e sacolas.',
      'Fabricamos em qualquer dimensão: balcões retos de 1,20m a 2,40m, em L para cantos, e em U para boxes maiores. Cores que combinam com a identidade da sua loja: branco, madeirado claro, madeirado escuro, cinza ou preto. E entregamos e montamos no seu ponto do Moda Center, Toritama Center ou qualquer loja do polo — sem você precisar parar de vender por mais de um dia.',
    ],
    img: '/media/ia/0309.webp',
    reverse: false,
  },
  {
    title: 'Araras e Expositores que Valorizam Cada Peça da Coleção',
    text: [
      'A forma como a roupa é exposta define se o cliente compra ou não. Araras de parede em metal cromado ou preto fosco com capacidade de 80kg por metro linear — suficiente para uma coleção completa sem dobrar as peças. Araras de chão com rodízios que permitem reorganizar o box conforme as promoções e as estações, sem precisar de ferramentas.',
      'Os expositores centrais em MDF com prateleiras inclinadas mostram blusas, calças e vestidos na altura dos olhos do cliente. Para acessórios, oferecemos nichos de parede, bandejas para bijuterias e expositores para cintos e bolsas. Tudo projetado para que o cliente veja, toque e queira levar — sem precisar pedir ajuda ao vendedor.',
    ],
    img: '/media/ia/0310.webp',
    reverse: true,
  },
  {
    title: 'Projeto 3D Gratuito: Veja Seu Box Antes de Gastar um Real',
    text: [
      'Antes de fabricar qualquer peça, você aprova o projeto completo em 3D. Nossa equipe visita seu box, mede cada centímetro, entende o fluxo de clientes e entrega o projeto em até 48h. Você vê exatamente onde vai ficar cada balcão, cada arara, cada expositor — e pode pedir ajustes antes de qualquer investimento.',
      'Esse processo elimina surpresas: sem móvel que não cabe, sem espaço desperdiçado, sem layout que atrapalha a circulação. E quando você aprovar, fabricamos e instalamos com nossa equipe própria, no horário que não atrapalha suas vendas. Seu box transformado em vitrine de alto padrão, sem dor de cabeça.',
    ],
    img: '/media/ia/0311.webp',
    reverse: false,
  },
];

const galleryImages = [
  {
    src: '/media/ia/0312.webp',
    alt: 'Balcão de atendimento para loja de roupas',
  },
  {
    src: '/media/ia/0313.webp',
    alt: 'Araras e expositores para box',
  },
  {
    src: '/media/ia/0314.webp',
    alt: 'Provador para loja de roupas',
  },
  {
    src: '/media/ia/0315.webp',
    alt: 'Expositores de parede para acessórios',
  },
];

export default function BoxSantaCruzPage() {
  const whatsappBtnRef = useRef<HTMLAnchorElement>(null);

  useSEO({
    title: 'Mobiliário para Boxes do Polo de Confecções | Santa Cruz do Capibaribe PE',
    description: 'Balcões, araras e expositores para boxes do polo de confecções em Santa Cruz do Capibaribe. Projeto 3D gratuito, montagem rápida. Moda Center e Toritama Center.',
    keywords: 'mobiliário box polo confecções, balcão loja roupas, araras Santa Cruz, Moda Center, projeto 3D gratuito',
    path: '/box-santa-cruz',
  });

  useSchemaLD({
    id: 'schema-box-santa-cruz',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Mobiliário para Boxes do Polo de Confecções | Santa Cruz do Capibaribe PE',
      description: 'Balcões, araras e expositores para boxes do polo de confecções. Projeto 3D gratuito, montagem rápida.',
      url: `${import.meta.env.VITE_SITE_URL ?? 'https://homeofficepremium.com'}/box-santa-cruz`,
      about: {
        '@type': 'Service',
        name: 'Mobiliário para Boxes do Polo de Confecções',
        provider: {
          '@type': 'Organization',
          name: 'Home Office Premium',
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'Av. Agamenon Magalhães, 1519',
            addressLocality: 'Caruaru',
            addressRegion: 'PE',
            postalCode: '55014-000',
            addressCountry: 'BR',
          },
        },
      },
    },
  });

  return (
    <div className="lp-page-wrapper min-h-screen bg-white">
      <section
        className="relative min-h-[640px] md:min-h-[700px] flex items-center"
        style={{
          backgroundImage: 'url(/media/ia/0316.webp)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/20"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-28 pb-16 w-full">
          <div className="max-w-2xl">
            <span className="inline-block bg-[#E8610A] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-5">
              Polo de Confecções · Santa Cruz do Capibaribe
            </span>
            <h1 className="text-white text-4xl md:text-5xl font-bold leading-tight mb-4">
              Transforme seu box em uma vitrine de alto padrão que vende mais.
            </h1>
            <p className="text-white/85 text-lg leading-relaxed mb-3">
              Balcões funcionais, araras que valorizam a mercadoria e layout inteligente que aproveita cada centímetro. Montagem rápida para não perder dia de feira.
            </p>
            <p className="text-white/70 text-sm mb-8">
              Projeto 3D gratuito · Equipe própria de montagem · Atendimento no Moda Center e Toritama Center
            </p>
            <a
              href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Boxes%20do%20Polo%20de%20Confec%C3%A7%C3%B5es%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#E8610A] hover:bg-[#C4500A] text-white font-bold px-8 py-4 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide"
            >
              <i className="ri-whatsapp-line text-lg"></i>
              SOLICITAR ORÇAMENTO E PROJETO 3D GRATUITO
            </a>
          </div>
        </div>
      </section>

      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-gray-900 text-2xl md:text-3xl font-bold">
              Por que lojistas do polo escolhem a <span className="text-[#E8610A]">Home Office Premium</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((b) => (
              <div key={b.title} className="bg-white rounded-xl p-6 flex flex-col gap-4 border border-gray-200">
                <div className="w-12 h-12 flex items-center justify-center bg-[#E8610A]/10 rounded-lg">
                  <i className={`${b.icon} text-[#E8610A] text-2xl`}></i>
                </div>
                <h3 className="text-gray-900 font-bold text-base leading-snug">{b.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{b.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#fafafa]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-gray-900 text-2xl md:text-3xl font-normal leading-tight">
              <span className="text-[#E8610A] font-semibold">Mobiliário Comercial</span> para o Maior Polo Têxtil do Brasil
            </h2>
            <div className="w-16 h-0.5 bg-[#E8610A] mx-auto mt-4"></div>
          </div>
          <div className="flex flex-col gap-12">
            {blocks.map((block, idx) => (
              <div key={idx} className={`flex flex-col ${block.reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-8 bg-white rounded-xl p-8 border border-gray-200`}>
                <div className="w-full lg:w-1/2 h-64 md:h-72 rounded-lg overflow-hidden flex-shrink-0">
                  <img src={block.img} alt={block.title} className="w-full h-full object-cover object-top" />
                </div>
                <div className="w-full lg:w-1/2">
                  <h3 className="text-gray-900 text-xl font-bold mb-4 leading-snug">{block.title}</h3>
                  <div className="flex flex-col gap-3">
                    {block.text.map((p, i) => (
                      <p key={i} className="text-gray-600 text-sm leading-relaxed">{p}</p>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-center mt-10">
            <a href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Boxes%20do%20Polo%20de%20Confec%C3%A7%C3%B5es%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#E8610A] hover:bg-[#C4500A] text-white font-bold px-10 py-4 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide">
              <i className="ri-whatsapp-line text-lg"></i>
              SOLICITAR ORÇAMENTO E PROJETO 3D GRATUITO
            </a>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#1a1a1a]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-[#E8610A] text-sm font-bold uppercase tracking-widest mb-4">Sem custo e sem compromisso</p>
          <h2 className="text-white text-3xl md:text-4xl font-bold leading-tight mb-4">
            Veja seu box transformado em 3D antes de gastar um real.
          </h2>
          <p className="text-gray-400 text-base mb-10">
            Nossa equipe visita seu ponto, mede o espaço e entrega o projeto 3D em até 48h. Você aprova, a gente fabrica e instala — sem perder dia de feira.
          </p>
          <a href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Boxes%20do%20Polo%20de%20Confec%C3%A7%C3%B5es%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold px-10 py-4 rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide">
            <i className="ri-whatsapp-line text-lg"></i>
            QUERO MEU PROJETO 3D GRATUITO
          </a>
        </div>
      </section>

      <section className="py-16 bg-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-gray-400 text-sm mb-2">Galeria</p>
            <h2 className="text-white text-2xl md:text-3xl font-bold">Mobiliário para <span className="text-[#E8610A]">Boxes do Polo</span></h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {galleryImages.map((img, idx) => (
              <div key={idx} className="w-full h-64 md:h-80 rounded-xl overflow-hidden">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-gray-400 text-xs font-semibold uppercase tracking-widest mb-3">ATENDIMENTO NO POLO DE CONFECÇÕES</p>
          <h2 className="text-gray-900 text-4xl md:text-5xl font-bold mb-4">Solicite seu orçamento</h2>
          <p className="text-gray-500 text-base mb-8">Projeto 3D gratuito incluso. Resposta em até 2 horas.</p>
          <a href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Boxes%20do%20Polo%20de%20Confec%C3%A7%C3%B5es%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 justify-center bg-[#25D366] hover:bg-[#1ebe5d] text-white font-semibold px-10 py-4 rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm mb-10">
            <i className="ri-whatsapp-line text-lg"></i>
            Chamar no WhatsApp
          </a>
          <div className="mt-10 text-gray-500 text-sm">
            <p>Av. Agamenon Magalhães, 1519 · Maurício de Nassau, Caruaru - PE · CEP 55014-000</p>
          </div>
        </div>
      </section>

      <footer className="bg-white py-8 border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-6 flex flex-col items-center gap-4">
          <img src="https://files.greatpages.com.br/arquivos/paginas_editor/98015-32d41c6ba8b126a2913863e7b342fb04.png" alt="Home Office Premium" className="h-16 w-auto object-contain" />
          <div className="flex items-center gap-4">
            <a href="https://www.instagram.com/homeoffice.premium/" target="_blank" rel="noopener noreferrer" className="cursor-pointer"><img src="https://files.greatpages.com.br/arquivos/paginas_editor/1-cc1918b73804ac63dbfe7624bf53ae73.png" alt="Instagram" className="w-8 h-8 object-contain" /></a>
            <a href="https://www.facebook.com/homeoffice.premium" target="_blank" rel="noopener noreferrer" className="cursor-pointer"><img src="https://files.greatpages.com.br/arquivos/paginas_editor/1-d39cee63d39805fee1ea3fe03b5a2e09.png" alt="Facebook" className="w-8 h-8 object-contain" /></a>
          </div>
        </div>
      </footer>

      <a ref={whatsappBtnRef} href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Boxes%20do%20Polo%20de%20Confec%C3%A7%C3%B5es%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!" target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-[9999] w-14 h-14 flex items-center justify-center bg-[#25D366] rounded-full cursor-pointer hover:scale-110 transition-transform duration-200" aria-label="WhatsApp" style={{ display: 'flex' }}>
        <i className="ri-whatsapp-line text-white text-2xl"></i>
      </a>
    </div>
  );
}
